import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Coins, QrCode, Copy, SignOut, Trophy, Users, Eye, Wallet } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/App';
import BrandLogo from '@/components/BrandLogo';

const cents = (c) => `€${((c || 0) / 100).toFixed(2)}`;

const safeReadJson = async (res) => {
  try {
    // Clone before reading: Sentry fetch instrumentation may drain the original stream.
    const text = await res.clone().text();
    if (!text) return {};
    try { return JSON.parse(text); } catch { return { _raw: text }; }
  } catch { return {}; }
};

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div
      className="bg-[#1a1508] border border-[#cba135]/15 rounded-lg p-5 shadow-[0_4px_18px_rgba(0,0,0,0.4)]"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="uppercase text-[10px] tracking-[0.22em] text-[#a8872b] font-semibold">{label}</span>
        {Icon && <Icon size={18} weight="duotone" className="text-[#cba135]/60" />}
      </div>
      <p className="heading-font text-3xl text-[#cba135] mt-2 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-[#cba135]/50 mt-1">{sub}</p>}
    </div>
  );
}

const statusBadge = (status) => {
  const map = {
    paid: 'bg-emerald-500/10 text-emerald-300',
    approved: 'bg-blue-500/10 text-blue-300',
    pending: 'bg-amber-500/10 text-amber-300',
    rejected: 'bg-red-500/10 text-red-300',
    no_purchase: 'bg-[#cba135]/10 text-[#cba135]/60',
  };
  const label = status === 'no_purchase' ? 'no purchase' : status;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${map[status] || 'bg-[#cba135]/10 text-[#cba135]/60'}`}>
      {label}
    </span>
  );
};

export default function PartnerDashboard({ user, token, onLogout }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [sRes, aRes] = await Promise.all([
        fetch(`${API}/partner/me/stats`, { headers: auth }),
        fetch(`${API}/partner/me/activity`, { headers: auth }),
      ]);
      const s = await safeReadJson(sRes);
      const a = await safeReadJson(aRes);
      if (sRes.ok) setStats(s);
      if (aRes.ok) setActivity(a.items || []);
    } catch (e) {
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async () => {
    if (!stats?.referralUrl) return;
    try {
      await navigator.clipboard.writeText(stats.referralUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0e0b04] flex items-center justify-center">
        <p className="text-[#cba135]/60">Loading dashboard…</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#0e0b04] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#cba135]/80 mb-4">Your partner account is not yet linked to an affiliate code.</p>
          <Button onClick={onLogout} variant="outline" className="border-[#cba135]/30 text-[#cba135]">Sign out</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0e0b04] text-[#cba135]">
      {/* Header */}
      <header className="bg-[#0e0b04]/80 backdrop-blur-xl border-b border-[#cba135]/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandLogo />
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#cba135]/70">
              <Coins size={16} weight="duotone" /> Partner Portal
            </div>
            <Button
              data-testid="partner-logout"
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="border-[#cba135]/20 text-[#cba135] hover:bg-[#cba135]/10"
            >
              <SignOut size={16} /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-[#cba135]/60 text-sm uppercase tracking-[0.22em]">Affiliate code</p>
          <h1 className="heading-font text-4xl sm:text-5xl text-[#cba135] mt-1">{stats.code}</h1>
          <p className="text-[#cba135]/70 mt-1">
            {stats.name || 'Partner'} · {(() => {
              const ticketC = stats.cpaTicketCents ?? stats.cpaAmountCents ?? 0;
              const parts = [];
              if (ticketC > 0) parts.push(`€${(ticketC / 100).toFixed(2)}/ticket`);
              if (stats.cpaRegistrationCents > 0) parts.push(`€${(stats.cpaRegistrationCents / 100).toFixed(2)}/reg`);
              if (stats.cpcCents > 0) parts.push(`€${(stats.cpcCents / 100).toFixed(2)}/click`);
              if (stats.revsharePercent > 0) parts.push(`${stats.revsharePercent}% rev`);
              return parts.join(' + ') || 'no reward configured';
            })()}
          </p>
        </motion.div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard testid="stat-clicks" label="Clicks" value={stats.clicks} icon={Eye} />
          <StatCard testid="stat-regs" label="Registrations" value={stats.registrations} icon={Users} />
          <StatCard testid="stat-cr" label="Conversion" value={`${stats.conversionPct}%`} sub="regs / clicks" />
          <StatCard
            testid="stat-paid"
            label="Paid rewards"
            value={stats.rewardsByStatus?.paid?.count || 0}
            icon={Trophy}
          />
          <StatCard
            testid="stat-earned"
            label="Earned"
            value={cents(stats.paidCents)}
            sub={`Pending: ${cents((stats.earnedCents || 0) - (stats.paidCents || 0))}`}
            icon={Wallet}
          />
        </div>

        {/* QR & Link */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 bg-[#1a1508] border border-[#cba135]/15 rounded-lg p-6 mb-8"
        >
          <div className="flex flex-col items-center">
            <div className="bg-white p-3 rounded">
              <img
                src={stats.qrUrl}
                alt={`QR code for ${stats.code}`}
                className="w-44 h-44 block"
                data-testid="partner-qr-img"
              />
            </div>
            <a
              href={stats.qrUrl}
              download={`affiliate-${stats.code}.png`}
              className="mt-3 text-xs uppercase tracking-wider text-[#cba135] hover:text-[#e6be4b] inline-flex items-center gap-1"
              data-testid="partner-qr-download"
            >
              <QrCode size={14} /> Download PNG
            </a>
          </div>
          <div className="flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.22em] text-[#a8872b] mb-2">Your referral link</p>
            <div className="bg-[#0e0b04] border border-[#cba135]/20 rounded px-3 py-2.5 font-mono text-sm text-[#e6be4b] break-all" data-testid="partner-referral-link">
              {stats.referralUrl}
            </div>
            <Button
              onClick={copyLink}
              size="sm"
              className="mt-3 self-start bg-[#cba135] hover:bg-[#b8902f] text-[#0e0b04] font-medium"
              data-testid="partner-copy-link"
            >
              <Copy size={14} /> Copy link
            </Button>
            <p className="text-xs text-[#cba135]/55 mt-4 leading-relaxed">
              Share this link or scan the QR with your audience — rewards are credited automatically as agreed. Payouts are sent by admin; contact them when you wish to be paid.
            </p>
          </div>
        </motion.div>

        {/* Activity */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="heading-font text-xl text-[#cba135] mb-3">Recent activity</h2>
          <div className="bg-[#1a1508] border border-[#cba135]/15 rounded-lg p-5">
            {activity.length === 0 ? (
              <p className="text-[#cba135]/60 text-sm" data-testid="partner-activity-empty">
                No referrals yet. Share your QR or link to get started.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[#a8872b] uppercase text-xs tracking-wider">
                    <tr className="border-b border-[#cba135]/10">
                      <th className="text-left py-2 px-2">User</th>
                      <th className="text-left py-2 px-2">Country</th>
                      <th className="text-left py-2 px-2">Joined</th>
                      <th className="text-center py-2 px-2">Status</th>
                      <th className="text-right py-2 px-2">Reward</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#cba135]/90">
                    {activity.map((row, idx) => {
                      const rowKey = `${row.joinedAt || ''}-${row.maskedName || ''}-${idx}`;
                      return (
                      <tr key={rowKey} className="border-b border-[#cba135]/5" data-testid={`partner-activity-row-${idx}`}>
                        <td className="py-3 px-2 font-mono">{row.maskedName}</td>
                        <td className="py-3 px-2">{row.country || '—'}</td>
                        <td className="py-3 px-2 text-[#cba135]/70">{timeAgo(row.joinedAt)}</td>
                        <td className="py-3 px-2 text-center">{statusBadge(row.rewardStatus)}</td>
                        <td className="py-3 px-2 text-right">{row.rewardAmountCents ? cents(row.rewardAmountCents) : '—'}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[10px] text-[#cba135]/40 mt-4 uppercase tracking-wider">
              Names are masked for privacy. Country is approximated from KYC data.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
